import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useNavigate, useLocation } from "react-router-dom";
import { makeStyles, styled } from "@mui/styles";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import Hidden from "@mui/material/Hidden";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import configData from "config/config.json";
import { getVersion } from "utils/miscUtils";
import sessionHelper from "services/sessionHelper"; // Ensure sessionHelper is imported
import { Typography } from "@mui/material";
import { userRoles } from "utils/constants";

const drawerWidth = 240;
const minDrawerWidth = 200;
const maxDrawerWidth = 480;
const mobileDrawerWith = "100%";

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    borderRight: "none",
    backgroundColor: `${theme.palette.secondary.main} !important`,
    width: ({ width }: any) => width,
    overflow: "auto",
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      width: mobileDrawerWith,
      zIndex: 999,
    },
  },
  resizeHandle: {
    position: "fixed",
    top: 0,
    height: "100vh",
    width: 6,
    cursor: "col-resize",
    zIndex: theme.zIndex.drawer + 2,
    "&:hover": {
      backgroundColor: "#EEEEEE33",
    },
    "&:active": {
      backgroundColor: "#EEEEEE55",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  closeIcon: {
    top: 0,
    right: 0,
    height: 36,
    width: 36,
    color: theme.palette.common.white,
  },
  link: {
    textAlign: "left",
    fontWeight: 400,
    color: (theme.palette.secondary as any).label,
    textDecoration: "none",
    cursor: "pointer",
    "& span": {
      fontSize: 13,
    },
  },
  selectedCell: {
    backgroundColor: "#3F3350",
  },
  selectedCellText: {
    color: `${theme.palette.common.white} !important`,
  },
  menuButton: {
    "&.Mui-focusVisible": {
      backgroundColor: "#EEEEEE22",
      outline: "2px solid rgba(255,255,255,0.55)",
      outlineOffset: "-2px",
      borderRadius: 4,
    },
  },
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const ExpandButton = ({ curr, selected }: any) => {
  const classes = useStyles({ width: drawerWidth });
  return curr === selected ? (
    <ExpandLess style={{ color: "white" }} className={classes.link} />
  ) : (
    <ExpandMore className={classes.link} />
  );
};

const sideItemWithoutMenu = [
  "dashboard",
  "configurations",
  "botghostTeamManagement",
  "logos",
  "allUsers",
  "ftuePackManagement",
  "fatigueConfig",
  "adminCredentials",
  "masterGame",
  "operatorGames",
  "platforms",
  "brands",
  "currency",
  "homeBanners",
  "splashScreen",
  "store",
  "storeBanners",
  "dailyRewards",
  "translation",
  "notifications",
  "sellPlayer",
  "quickBet",
  "reCalculateTeamRatings",
  "userManagement",
  "leagues",
  "packCategory",

  "vaultRewardsAndDropRates",
  "cardBackground",
  "reportSchedulers",
  "tutorials",
  "operatorLogos",
];

const Sidebar = ({
  open,
  onToggleOpen,
  role,
  width = drawerWidth,
  onWidthChange,
}: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles({ width });

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;

    const onMouseMove = (moveEvent) => {
      const nextWidth = startWidth + (moveEvent.clientX - startX);
      const clamped = Math.min(
        maxDrawerWidth,
        Math.max(minDrawerWidth, nextWidth),
      );
      onWidthChange && onWidthChange(clamped);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [selectedSubmenu, setSelectedSubmenuMenu] = useState(0);
  const [list, setList] = useState({});

  const menuItemRefs = React.useRef<any[]>([]);
  const typeaheadRef = React.useRef({ buffer: "", time: 0 });

  const focusMenuItemAt = (idx) => {
    const el = menuItemRefs.current[idx];
    if (el) {
      el.focus();
      el.scrollIntoView({ block: "nearest" });
    }
  };

  const handleMenuKeyDown = (e) => {
    const keys = Object.keys(list);
    if (keys.length === 0) return;

    const activeEl = document.activeElement;
    const currentIndex = menuItemRefs.current.findIndex(
      (el) => el === activeEl,
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusMenuItemAt(currentIndex < 0 ? 0 : (currentIndex + 1) % keys.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      focusMenuItemAt(
        currentIndex < 0
          ? keys.length - 1
          : (currentIndex - 1 + keys.length) % keys.length,
      );
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      focusMenuItemAt(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      focusMenuItemAt(keys.length - 1);
      return;
    }

    if (
      e.key.length !== 1 ||
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      !/\S/.test(e.key)
    ) {
      return;
    }

    const now = Date.now();
    const ta = typeaheadRef.current;
    if (now - ta.time > 600) ta.buffer = "";
    ta.time = now;
    ta.buffer += e.key.toLowerCase();

    const titles = keys.map((k) => (list[k].title || "").toLowerCase());
    const allSame = [...ta.buffer].every((c) => c === ta.buffer[0]);
    const query = allSame ? ta.buffer[0] : ta.buffer;

    let startFrom = 0;
    if (currentIndex >= 0) {
      startFrom = allSame ? currentIndex + 1 : currentIndex;
    }

    let matchIndex = -1;
    for (let i = 0; i < keys.length; i++) {
      const idx = (startFrom + i) % keys.length;
      if (titles[idx].startsWith(query)) {
        matchIndex = idx;
        break;
      }
    }

    if (matchIndex >= 0) {
      e.preventDefault();
      focusMenuItemAt(matchIndex);
    }
  };

  const userRole = role || sessionHelper.role; // Get the user's role

  const handleChange = (link, index) => {
    setSelectedMenu(selectedMenu === index ? null : index);
    setSelectedSubmenuMenu(null);
    if (sideItemWithoutMenu.includes(link)) {
      navigate(configData.sideBarLinks[link].menu[0].url);
      setSelectedSubmenuMenu(index);
    }
  };

  const handleClick = (url, index) => {
    setSelectedSubmenuMenu(index);
    navigate(url);
  };

  const [version, setVersion] = useState("");

  React.useEffect(() => {
    const _list = {};

    const sortedLinks = Object.keys(configData.sideBarLinks).sort((a, b) => {
      if (a === "dashboard") return -1;
      if (b === "dashboard") return 1;
      return configData.sideBarLinks[a].title.localeCompare(
        configData.sideBarLinks[b].title,
      );
    });

    // Filter list items by role and hide empty folders
    for (const link of sortedLinks) {
      const menu = configData.sideBarLinks[link].menu.filter(
        (item) =>
          item.requiredRole.length === 0 || // No restriction
          item.requiredRole.includes(userRole) || // Matches user role
          [userRoles.SUPER_ADMIN_DELETE, userRoles.SUPER_ADMIN].includes(
            userRole,
          ), // Allow SUPER_ADMIN
      );

      // Only add to _list if there are accessible menu items
      if (menu.length > 0) {
        _list[link] = {
          ...configData.sideBarLinks[link],
          menu: menu,
        };
      }
    }

    const url = location.pathname.split("/");
    const sideBarKeys = Object.keys(_list);

    for (const item of sideBarKeys) {
      let subMenu = _list[item]?.menu.find(
        (el) =>
          el.url.includes(url[url?.length - 1]) &&
          el?.url?.split("/")[1] === url[1],
      );
      let index = _list[item]?.menu.indexOf(subMenu);
      if (index > -1) {
        setSelectedMenu(sideBarKeys.indexOf(item));
        setSelectedSubmenuMenu(index);
        break;
      }
    }

    setList(_list);
    getVersion().then(setVersion);
  }, [userRole, location.pathname]);

  React.useEffect(() => {
    const onGlobalKeyDown = (e) => {
      if (!open) return;

      const active = document.activeElement;
      const nothingFocused =
        !active ||
        active === document.body ||
        active === document.documentElement;
      if (!nothingFocused) return;

      const isChar =
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        /^[a-zA-Z0-9]$/.test(e.key);
      const isNav = ["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key);
      if (!isChar && !isNav) return;

      handleMenuKeyDown(e);
    };

    document.addEventListener("keydown", onGlobalKeyDown);
    return () => document.removeEventListener("keydown", onGlobalKeyDown);
  }, [list, open]);

  return (
    <>
      {open && (
        <div
          className={classes.resizeHandle}
          style={{ left: width - 3 }}
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
        />
      )}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        classes={{ paper: classes.drawerPaper }}
      >
      <Hidden smUp>
        <DrawerHeader>
          <IconButton onClick={onToggleOpen} className={classes.closeIcon}>
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
      </Hidden>

      <List onKeyDown={handleMenuKeyDown}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
          }}
        >
          <Typography
            sx={{
              color: "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            PBF CMS Menu
          </Typography>

          {version ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                px: "8px",
                py: "2px",
                borderRadius: "999px",
                backgroundColor: "#FFFFFF14",
                border: "1px solid #FFFFFF26",
                color: "rgba(255,255,255,0.9)",
                fontSize: 13.5,
                fontWeight: 600,
                letterSpacing: "0.3px",
                lineHeight: 1.6,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#4ADE80",
                  boxShadow: "0 0 0 2px #4ADE8033",
                  flexShrink: 0,
                }}
              />
              {version}
            </Box>
          ) : null}
        </Box>
        {Object.keys(list).map((link, index) => (
          <Box key={list[link].title}>
            <ListItemButton
              ref={(el) => (menuItemRefs.current[index] = el)}
              className={classes.menuButton}
              onClick={() => handleChange(link, index)}
            >
              <ListItemIcon style={{ minWidth: "36px" }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      index === selectedMenu ? "#FFFFFF" : "#EEEEEE33",
                    color: index === selectedMenu ? "#3F3350" : "#FFFFFF",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {(list[link].title || "").charAt(0).toUpperCase()}
                </Box>
              </ListItemIcon>
              <ListItemText
                className={classNames(classes.link, {
                  [classes.selectedCellText]: index === selectedMenu,
                })}
                primary={list[link].title}
              />

              {/* <ExpandButton curr={index} selected={selectedMenu} /> */}
              {!sideItemWithoutMenu.includes(link) && (
                <ExpandButton curr={index} selected={selectedMenu} />
              )}
            </ListItemButton>
            {!sideItemWithoutMenu.includes(link) && (
              <Collapse
                in={index === selectedMenu}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {list[link].menu.map((menuItem, subIndex) => (
                    <ListItemButton
                      sx={{ pl: 4 }}
                      key={menuItem.title}
                      onClick={() => handleClick(menuItem.url, subIndex)}
                      className={classNames({
                        [classes.selectedCell]: subIndex === selectedSubmenu,
                      })}
                    >
                      <ListItemIcon style={{ minWidth: "20px" }} />
                      <ListItemText
                        className={classNames(classes.link, {
                          [classes.selectedCellText]:
                            subIndex === selectedSubmenu,
                        })}
                        primary={menuItem.title}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
        </List>
      </Drawer>
    </>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool,
  onToggleOpen: PropTypes.func,
  role: PropTypes.any,
  width: PropTypes.number,
  onWidthChange: PropTypes.func,
};

Sidebar.defaultProps = {
  open: true,
};

export default Sidebar;
export { drawerWidth, minDrawerWidth, maxDrawerWidth };
